/**
 * HMAC-signed capability used by a Cast receiver to act for one live playback
 * session. The backing session document remains authoritative on every use.
 */
export const RECEIVER_BADGE_TTL_SECONDS = 12 * 60 * 60;
const encoder = new TextEncoder();
function base64UrlEncode(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function base64UrlDecode(text) {
    const base64 = text.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1)
        bytes[index] = binary.charCodeAt(index);
    return bytes;
}
async function hmacKey(secret) {
    return crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}
export async function signReceiverBadge(claims, secret) {
    const payload = base64UrlEncode(encoder.encode(JSON.stringify(claims)));
    const key = await hmacKey(secret);
    const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(payload));
    return `${payload}.${base64UrlEncode(new Uint8Array(signature))}`;
}
export async function verifyReceiverBadge(badge, secret, nowUnixSeconds = Math.floor(Date.now() / 1000)) {
    const dot = badge.indexOf(".");
    if (dot <= 0 || dot === badge.length - 1)
        return { ok: false, reason: "malformed" };
    const payload = badge.slice(0, dot);
    let claims;
    let signature;
    try {
        claims = JSON.parse(new TextDecoder().decode(base64UrlDecode(payload)));
        signature = base64UrlDecode(badge.slice(dot + 1));
    }
    catch {
        return { ok: false, reason: "malformed" };
    }
    const valid = await crypto.subtle.verify("HMAC", await hmacKey(secret), signature, encoder.encode(payload));
    if (!valid)
        return { ok: false, reason: "bad-signature" };
    if (typeof claims.accountId !== "string" ||
        claims.accountId.length === 0 ||
        typeof claims.sessionId !== "string" ||
        claims.sessionId.length === 0 ||
        typeof claims.exp !== "number") {
        return { ok: false, reason: "malformed" };
    }
    if (claims.exp <= nowUnixSeconds)
        return { ok: false, reason: "expired" };
    return { ok: true, claims };
}
